import {
  addDays,
  addHours,
  addYears,
  eachMonthOfInterval,
  eachYearOfInterval,
  endOfDecade,
  endOfYear,
  format,
  getYear,
  Locale,
  set,
  startOfYear,
  subDays,
  subHours,
  subYears,
} from 'date-fns';
import { CalendarInterval, CalendarPeriodName, CalendarResolution } from './types';
import {
  DAY_RESOLUTION,
  DECADE_RESOLUTION,
  HOUR_RESOLUTION,
  MINUTE_RESOLUTION,
  MONTH_RESOLUTION,
  YEAR_RESOLUTION,
} from './resolution';

export interface CalendarPeriodList {
  name: string;
  prev: Date;
  next: Date;
  top: CalendarPeriodName | null;
  resolution: CalendarResolution;
  keepOffset: boolean;
  periods: CalendarPeriod[];
}

export interface CalendarPeriod {
  interval: CalendarInterval;
  name: string;
}

function getMinutePeriods(date: Date): CalendarPeriodList {
  return {
    name: format(date, 'HH'),
    prev: subHours(date, 1),
    next: addHours(date, 1),
    top: 'hour',
    resolution: MINUTE_RESOLUTION,
    keepOffset: false,
    periods: Array.from({ length: 60 }, (_, minutes) => ({
      interval: MINUTE_RESOLUTION.resolve(set(date, { minutes })),
      name: minutes.toString().padStart(2, '0'),
    })),
  };
}

function getHourPeriods(date: Date): CalendarPeriodList {
  return {
    name: format(date, 'dd'),
    prev: subDays(date, 1),
    next: addDays(date, 1),
    top: 'day',
    resolution: HOUR_RESOLUTION,
    keepOffset: true,
    periods: Array.from({ length: 24 }, (_, hours) => ({
      interval: HOUR_RESOLUTION.resolve(set(date, { hours })),
      name: hours.toString().padStart(2, '0'),
    })),
  };
}

function getMonthPeriods(date: Date, locale: Locale): CalendarPeriodList {
  return {
    name: format(date, 'yyyy', { locale }),
    prev: subYears(date, 1),
    next: addYears(date, 1),
    top: 'year',
    resolution: MONTH_RESOLUTION,
    keepOffset: false,
    periods: eachMonthOfInterval({ start: startOfYear(date), end: endOfYear(date) }).map((month) => ({
      interval: MONTH_RESOLUTION.resolve(month),
      name: format(month, 'MMM', { locale }),
    })),
  };
}

function getYearPeriods(date: Date): CalendarPeriodList {
  const start = subYears(endOfDecade(date), 10);
  const end = addYears(endOfDecade(date), 1);
  return {
    name: `${format(start, 'yyyy')}~${format(end, 'yyyy')}`,
    prev: subYears(date, 10),
    next: addYears(date, 10),
    top: 'decade',
    resolution: YEAR_RESOLUTION,
    keepOffset: true,
    periods: eachYearOfInterval({ start, end }).map((year) => ({
      interval: YEAR_RESOLUTION.resolve(year),
      name: format(year, 'yyyy'),
    })),
  };
}

function getDecadePeriods(date: Date): CalendarPeriodList {
  const baseYear = Math.floor(getYear(endOfDecade(date)) / 100) * 100;
  const periods = Array.from({ length: 12 }, (_, i) => {
    const yearNum = baseYear - 10 + i * 10;
    const year = new Date();
    year.setFullYear(yearNum);
    const interval = DECADE_RESOLUTION.resolve(year);
    return {
      interval,
      name: `${format(interval.from, 'yyyy')}~${format(interval.to, 'yyyy')}`,
    };
  });
  return {
    name: `${format(periods[0].interval.from, 'yyyy')}~${format(periods[periods.length - 1].interval.to, 'yyyy')}`,
    prev: subYears(date, 120),
    next: addYears(date, 120),
    top: null,
    resolution: DECADE_RESOLUTION,
    keepOffset: true,
    periods,
  };
}

export function getPeriods(name: CalendarPeriodName, date: Date, locale: Locale): CalendarPeriodList {
  switch (name) {
    case 'minute':
      return getMinutePeriods(date);
    case 'hour':
      return getHourPeriods(date);
    case 'day':
      return {
        name: '',
        prev: date,
        next: date,
        top: null,
        resolution: DAY_RESOLUTION,
        keepOffset: false,
        periods: [],
      };
    case 'month':
      return getMonthPeriods(date, locale);
    case 'year':
      return getYearPeriods(date);
    case 'decade':
      return getDecadePeriods(date);
  }
}
